import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { connectField } from 'dynamic-uniforms'
import { ChangeEvent, useCallback } from 'react'
import { Markdown } from '../../Markdown'
import { useCiboAutoForm } from './CiboAutoFormContext'
import { ClearButton } from './ClearButton'
import { FieldLabel } from './FieldLabel'
import { filterDOMProps } from './filterDOMProps'
import { ValidationMessages } from './ValidationMessages'

export interface ClearableNumberFieldProps {
  value: number | undefined | null
  onChange: (value: number | undefined) => void
  label?: string
  disabled?: boolean
  error?: any
  helperText?: string
  required?: boolean
  name: string
  placeholder?: string
  [key: string]: any // uniforms injects many props
}

export function ClearableNumberInput({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled,
  required,
  placeholder,
  description,
  ...props
}: ClearableNumberFieldProps) {
  const uiParams = props['ui-params'] || {}
  const isInteger = props.field?.type === 'integer'

  const { touch } = useCiboAutoForm()
  // @ts-ignore value might be string because it's really a text input
  const canClear = !disabled && value != null && value !== ''

  const handleZeroValueCheckbox = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange(0)
      } else {
        onChange(undefined)
      }
      touch(props.name)
    },
    [onChange, props.name, touch]
  )

  return (
    <>
      <FormControl fullWidth {...filterDOMProps(props)} disabled={disabled}>
        {label && <FieldLabel label={label} required={required} />}

        <TextField
          data-testid={`number-field-${props.name}`}
          {...filterDOMProps(props)}
          type="number"
          value={value ?? ''}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          helperText={helperText}
          onChange={e => {
            const v = e.target.value
            // Empty string → undefined (Uniforms best practice)
            if (v === '') onChange(undefined)
            else onChange(isInteger ? Math.round(Number(v)) : Number(v))

            touch(props.name)
          }}
          onBlur={() => touch(props.name)}
          slotProps={{
            htmlInput: isInteger ? { step: 1 } : undefined,
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography>{uiParams.units || ''}</Typography>

                  {canClear && (
                    <ClearButton
                      tooltipKey="clearButton.value"
                      onClick={() => onChange(undefined)}
                    />
                  )}
                </InputAdornment>
              ),
            },
          }}
          fullWidth
        />

        <ValidationMessages fieldName={props.name} error={error} />

        {!!description && (
          <Markdown typographyProps={{ variant: 'caption' }}>{description}</Markdown>
        )}
      </FormControl>

      {uiParams.zeroValueCta && (
        <FormControlLabel
          control={<Checkbox checked={value === 0} onChange={handleZeroValueCheckbox} />}
          label={uiParams.zeroValueCta}
        />
      )}
    </>
  )
}

export const NumberField = connectField(ClearableNumberInput)
